/**
 * TMS Report — By Date
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, RefreshCcw, Loader2, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';

type JobRow = { id: string; status: string; jobType: string; containerSize: string; customerName: string; driverName: string; arAmount: number; createdAt: string; deliveryDate: string };

function normalise(r: Record<string, unknown>): JobRow {
  const d = (r.data ?? {}) as Record<string, unknown>;
  const rawSize = String(r.containerSize ?? r.container_size ?? d.container_size ?? d.containerSize ?? '');
  // Normalise "20'", "40'", "40HC", "45'" etc. → "20" | "40" | "40HC" | "45"
  const sz = rawSize.replace("'", '').replace("'", '').trim();
  const containerSize = sz.startsWith('40H') ? '40HC' : sz === '20' ? '20' : sz === '40' ? '40' : sz === '45' ? '45' : sz || '?';
  return {
    id: String(r.id ?? ''),
    status: String(r.status ?? 'draft'),
    jobType: String(r.jobType ?? r.job_type ?? '').toUpperCase() || 'OTH',
    containerSize,
    customerName: String(r.customerName ?? r.customer_name ?? d.client_code ?? ''),
    driverName: String(r.driverName ?? r.driver_name ?? d.driver_name ?? ''),
    arAmount: Number(r.arAmount ?? r.ar_amount ?? 0),
    createdAt: String(r.createdAt ?? r.created_at ?? '').slice(0, 10),
    deliveryDate: String(r.deliveryDate ?? r.delivery_date ?? '').slice(0, 10),
  };
}

// Column definitions — type+size pairs to show in the table
const TYPE_GROUPS: { type: string; sizes: string[] }[] = [
  { type: 'IMP', sizes: ['20', '40'] },
  { type: 'EXP', sizes: ['20', '40'] },
  { type: 'RET', sizes: ['20', '40'] },
  { type: 'COL', sizes: ['20', '40'] },
  { type: 'LOC', sizes: ['—'] },
  { type: 'OTH', sizes: ['—'] },
];
// Flat column keys e.g. ['IMP_20', 'IMP_40', 'EXP_20', ...]
const COL_KEYS = TYPE_GROUPS.flatMap((g) => g.sizes.map((s) => `${g.type}_${s}`));

function colKey(jobType: string, containerSize: string): string {
  const base = ['20', '40', '40HC', '45'].includes(containerSize) ? containerSize : '—';
  const sz = base === '40HC' ? '40' : base; // merge 40HC→40 for simple display
  const key = `${jobType}_${sz}`;
  return COL_KEYS.includes(key) ? key : `${jobType}_—`;
}

export default function TmsReportDatePage() {
  const today = new Date().toISOString().slice(0, 10);
  const d30 = new Date(); d30.setDate(d30.getDate() - 29);
  const d30Str = d30.toISOString().slice(0, 10);

  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(d30Str);
  const [dateTo, setDateTo] = useState(today);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', sort: 'created_at', order: 'desc' });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get<{ items?: Record<string, unknown>[] }>(`/jobs?${params}`);
      const raw = Array.isArray(res) ? res : (res.items ?? []);
      setJobs(raw.map(normalise));
    } catch { setJobs([]); } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  function bucketKey(date: string): string {
    if (!date) return 'Unknown';
    if (groupBy === 'month') return date.slice(0, 7);
    if (groupBy === 'week') {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const mon = new Date(d.setDate(diff));
      return mon.toISOString().slice(0, 10);
    }
    return date.slice(0, 10);
  }

  const filtered = useMemo(() => jobs.filter((j) => {
    const pivot = j.createdAt;
    if (dateFrom && pivot < dateFrom) return false;
    if (dateTo && pivot > dateTo) return false;
    return true;
  }), [jobs, dateFrom, dateTo]);

  type DateStat = { bucket: string; total: number; byCols: Record<string, number>; byStatus: Record<string, number>; ar: number };

  const rows = useMemo(() => {
    const map = new Map<string, DateStat>();
    for (const j of filtered) {
      const bk = bucketKey(j.createdAt);
      const s = map.get(bk) ?? { bucket: bk, total: 0, byCols: {}, byStatus: {}, ar: 0 };
      s.total++;
      s.ar += j.arAmount;
      const ck = colKey(j.jobType, j.containerSize);
      s.byCols[ck] = (s.byCols[ck] ?? 0) + 1;
      s.byStatus[j.status] = (s.byStatus[j.status] ?? 0) + 1;
      map.set(bk, s);
    }
    return [...map.values()].sort((a, b) => b.bucket.localeCompare(a.bucket));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, groupBy]);

  const totalJobs = rows.reduce((s, r) => s + r.total, 0);
  const maxJobs = rows.reduce((m, r) => Math.max(m, r.total), 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as 'day' | 'week' | 'month')}>
          <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">By Day</SelectItem>
            <SelectItem value="week">By Week</SelectItem>
            <SelectItem value="month">By Month</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[150px] text-xs" />
        <span className="text-muted-foreground text-xs">—</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[150px] text-xs" />
        <Button variant="outline" size="sm" className="h-8" onClick={fetchJobs} disabled={loading}>
          <RefreshCcw className="h-3.5 w-3.5" />
        </Button>
        <Badge variant="secondary" className="text-xs">{tableRows(rows).length} periods · {totalJobs} jobs</Badge>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <Calendar className="h-8 w-8 mx-auto mb-3 opacity-30" />
              No jobs found for this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {/* Row 1: merged type headers */}
                  <tr className="bg-muted/60 border-b">
                    <th className="text-left py-1.5 px-3 font-medium text-muted-foreground" rowSpan={2}>{groupBy === 'day' ? 'Date' : groupBy === 'week' ? 'Week' : 'Month'}</th>
                    <th className="text-right py-1.5 px-2 font-medium text-muted-foreground" rowSpan={2}>Total</th>
                    <th className="text-center py-1.5 px-2 font-medium text-muted-foreground" rowSpan={2}>Trend</th>
                    {TYPE_GROUPS.map((g) => (
                      <th key={g.type} colSpan={g.sizes.length} className="text-center py-1.5 px-1 font-medium border-l">{g.type}</th>
                    ))}
                    <th className="text-right py-1.5 px-2 font-medium text-muted-foreground" rowSpan={2}>AR (SGD)</th>
                  </tr>
                  {/* Row 2: size sub-headers */}
                  <tr className="bg-muted/40 border-b text-muted-foreground">
                    {TYPE_GROUPS.map((g) =>
                      g.sizes.map((sz, si) => (
                        <th key={`${g.type}_${sz}`} className={`text-center py-1 px-2 font-normal ${si === 0 ? 'border-l' : ''}`}>{sz}</th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.bucket} className="border-t hover:bg-muted/20">
                      <td className="py-2 px-3 font-mono font-medium">{r.bucket}</td>
                      <td className="py-2 px-2 text-right font-semibold">{r.total}</td>
                      <td className="py-2 px-2 w-24">
                        <div className="h-2 bg-primary/20 rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.round((r.total / maxJobs) * 100)}%` }} />
                        </div>
                      </td>
                      {COL_KEYS.map((ck, ci) => {
                        const grpIdx = TYPE_GROUPS.findIndex((g) => ck.startsWith(g.type + '_'));
                        const isFirst = grpIdx >= 0 && TYPE_GROUPS[grpIdx]?.sizes[0] === ck.split('_')[1];
                        return (
                          <td key={ck} className={`py-2 px-2 text-center text-muted-foreground ${isFirst ? 'border-l' : ''}`}>
                            {r.byCols[ck] ? <span className="font-semibold text-foreground">{r.byCols[ck]}</span> : <span className="opacity-30">—</span>}
                          </td>
                        );
                      })}
                      <td className="py-2 px-2 text-right font-mono">{r.ar > 0 ? r.ar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/40 border-t-2">
                  <tr>
                    <td className="py-2 px-3 font-semibold text-sm">Total</td>
                    <td className="py-2 px-2 text-right font-semibold">{totalJobs}</td>
                    <td />
                    {COL_KEYS.map((ck, ci) => {
                      const grpIdx = TYPE_GROUPS.findIndex((g) => ck.startsWith(g.type + '_'));
                      const isFirst = grpIdx >= 0 && TYPE_GROUPS[grpIdx]?.sizes[0] === ck.split('_')[1];
                      const sum = rows.reduce((s, r) => s + (r.byCols[ck] ?? 0), 0);
                      return (
                        <td key={ck} className={`py-2 px-2 text-center font-semibold ${isFirst ? 'border-l' : ''}`}>{sum || '—'}</td>
                      );
                    })}
                    <td className="py-2 px-2 text-right font-semibold font-mono">
                      {rows.reduce((s, r) => s + r.ar, 0) > 0
                        ? rows.reduce((s, r) => s + r.ar, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function tableRows<T>(rows: T[]) { return rows; }
